import { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import api from "@/utils/api";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  ArrowLeft, Upload, Loader2, Activity, User, Calendar, ChevronRight,
  CheckCircle, AlertTriangle, Download, Ruler,
} from "lucide-react";
import useTheme from "@/utils/useTheme";
import ToolPageNavbar from "@/components/sections/ToolPageNavbar";
import Footer from "@/components/sections/Footer";
import PaywallModal from "@/components/PaywallModal";
import DOBPicker from "@/components/DOBPicker";
import { ageFromDOB, toISODate, parseISODate, formatAgeDisplay } from "@/utils/age";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BoneAgePage = () => {
  useTheme();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [boneResult, setBoneResult] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const fileInputRef = useRef(null);
  const [paywallOpen, setPaywallOpen] = useState(false);
  const [creditBalance, setCreditBalance] = useState(0);

  const [boneForm, setBoneForm] = useState({
    name: searchParams.get("name") || "",
    sex: searchParams.get("sex") || "",
  });
  const [dob, setDob] = useState(parseISODate(searchParams.get("dob")));
  const [selectedFile, setSelectedFile] = useState(null);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const downloadPDF = async () => {
    setPdfLoading(true);
    try {
      const response = await api.post(`${API}/boneah/generate-report`, {
        bone_data: boneResult || null,
        height_data: null,
      }, { responseType: "blob", timeout: 30000 });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = `BoneAge_Report_${boneResult?.name || "Kid"}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Report downloaded!");
    } catch (error) {
      if (error.response?.status === 402) {
        try {
          const me = await api.get(`${API}/payments/me`);
          setCreditBalance(me.data.balance || 0);
        } catch (_err) { /* ignore */ }
        setPaywallOpen(true);
        return;
      }
      let msg = "Failed to generate report";
      if (error.code === "ERR_NETWORK") msg = "Network error — please check your internet connection.";
      else if (error.response?.data) {
        try { const txt = await error.response.data.text(); msg = JSON.parse(txt).detail || msg; } catch (_err) { /* keep default msg */ }
      }
      toast.error(msg);
    } finally {
      setPdfLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      const reader = new FileReader();
      reader.onload = (ev) => setImagePreview(ev.target.result);
      reader.readAsDataURL(file);
    }
  };

  const compressImage = (file) => new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (ev) => {
      const img = new window.Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        const MAX = 512;
        let w = img.width, h = img.height;
        if (w > MAX || h > MAX) {
          if (w > h) { h = Math.round(h * MAX / w); w = MAX; }
          else { w = Math.round(w * MAX / h); h = MAX; }
        }
        canvas.width = w; canvas.height = h;
        canvas.getContext("2d").drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL("image/png"));
      };
      img.onerror = () => resolve(ev.target.result);
      img.src = ev.target.result;
    };
    reader.onerror = () => resolve(null);
    reader.readAsDataURL(file);
  });

  const handleBoneSubmit = async (e) => {
    e.preventDefault();
    if (!selectedFile) { toast.error("Please upload an X-ray image"); return; }
    if (!boneForm.name || !dob || boneForm.sex === "") { toast.error("Please fill all fields"); return; }
    const ageInfo = ageFromDOB(dob);
    if (!ageInfo || ageInfo.decimalYears <= 0 || ageInfo.decimalYears > 18) {
      toast.error("Date of birth must give an age between 0 and 18 years");
      return;
    }
    setLoading(true);
    setBoneResult(null);
    try {
      const imageBase64 = await compressImage(selectedFile);
      if (!imageBase64) throw new Error("Failed to process image");
      const response = await api.post(`${API}/boneah/predict-bone-age-b64`, {
        image_base64: imageBase64,
        name: boneForm.name,
        age: ageInfo.decimalYears,
        sex: parseInt(boneForm.sex),
      }, { timeout: 120000 });
      setBoneResult(response.data);
      toast.success("Bone age prediction complete!");
    } catch (error) {
      let msg = "Prediction failed. Please try again.";
      if (error.code === "ERR_NETWORK" || error.message?.includes("Network")) {
        msg = "Network error — please check your internet connection and try again.";
      } else if (error.code === "ECONNABORTED") {
        msg = "Request timed out — your image may be too large.";
      } else if (error.response?.data?.detail) {
        msg = error.response.data.detail;
      }
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const proceedToGrowth = () => {
    if (!boneResult) return;
    const params = new URLSearchParams({
      name: boneResult.name || "",
      bone_age: String(boneResult.bone_age_years || ""),
      chronological_age: String(boneResult.known_age || ""),
      gender: boneResult.sex === 1 ? "female" : "male",
      xray: "1",  // marks that bone_age came from a real X-ray prediction
    });
    const dobIso = toISODate(dob);
    if (dobIso) params.set("dob", dobIso);
    navigate(`/tools/growth?${params.toString()}`);
  };

  return (
    <div className="min-h-screen dark bg-mesh">
      <ToolPageNavbar />
      <main className="max-w-4xl mx-auto px-6 pt-28 pb-16">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <div className="text-center mb-8">
            <Link to="/" className="inline-flex items-center gap-2 text-white/40 hover:text-[#4ECDC4] transition-colors mb-6 text-sm" data-testid="back-to-home">
              <ArrowLeft className="w-4 h-4" /> Back to tots360
            </Link>
            <h1 className="font-display text-4xl md:text-5xl text-white mb-3" data-testid="bone-age-page-title">
              Bone Age <span className="text-[#4ECDC4]">Prediction</span>
            </h1>
            <p className="text-white/50 text-lg">AI-powered skeletal maturity assessment from a single hand X-ray</p>
          </div>

          <Card className="bg-white/[0.03] border border-white/10 backdrop-blur-xl rounded-2xl">
            <CardHeader>
              <CardTitle className="text-xl text-white font-display">Upload Hand X-Ray</CardTitle>
              <CardDescription className="text-white/40">Upload a left-hand X-ray for AI bone age determination</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleBoneSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2 text-white/70"><User className="w-4 h-4 text-[#4ECDC4]" /> Kid Name</Label>
                    <Input placeholder="Enter name" value={boneForm.name}
                      onChange={(e) => setBoneForm(p => ({ ...p, name: e.target.value }))}
                      className="bg-white/5 border-white/10 text-white placeholder:text-white/30" data-testid="bone-input-name" />
                  </div>
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2 text-white/70"><Calendar className="w-4 h-4 text-[#4ECDC4]" /> Date of Birth</Label>
                    <DOBPicker value={dob} onChange={setDob} testId="bone-dob" />
                  </div>
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2 text-white/70"><User className="w-4 h-4 text-[#4ECDC4]" /> Sex</Label>
                    <Select value={boneForm.sex} onValueChange={(v) => setBoneForm(p => ({ ...p, sex: v }))}>
                      <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid="bone-select-sex">
                        <SelectValue placeholder="Select sex" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#1a1a2e] border-white/10">
                        <SelectItem value="0" className="text-white">Male</SelectItem>
                        <SelectItem value="1" className="text-white">Female</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div
                  className="relative border-2 border-dashed border-white/20 rounded-2xl p-8 text-center cursor-pointer hover:border-[#4ECDC4]/50 transition-colors"
                  onClick={() => fileInputRef.current?.click()} data-testid="xray-upload-area"
                >
                  <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleFileChange} />
                  {imagePreview ? (
                    <div className="space-y-3">
                      <img src={imagePreview} alt="X-ray preview" className="max-h-64 mx-auto rounded-xl object-contain" />
                      <p className="text-sm text-[#4ECDC4]">{selectedFile?.name} | Click to change</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <Upload className="w-12 h-12 text-white/20 mx-auto" />
                      <p className="text-white/40">Click to upload left-hand X-ray image</p>
                      <p className="text-xs text-white/20">Supports JPG, PNG, DICOM</p>
                    </div>
                  )}
                </div>

                <Button type="submit" className="w-full bg-[#4ECDC4] hover:bg-[#3dbdb5] text-[#0a1628] text-lg py-6 rounded-xl font-semibold"
                  disabled={loading} data-testid="bone-submit-btn">
                  {loading ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Analyzing X-Ray...</>) : "Predict Bone Age"}
                </Button>
              </form>
            </CardContent>
          </Card>

          <AnimatePresence>
            {boneResult && (
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="mt-6">
                <Card className="bg-white/[0.03] border border-white/10 backdrop-blur-xl rounded-2xl" data-testid="bone-result-card">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-white font-display">
                      <Activity className="w-5 h-5 text-[#4ECDC4]" /> Bone Age Results for {boneResult.name}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="p-4 bg-[#4ECDC4]/10 rounded-xl border border-[#4ECDC4]/30 text-center">
                        <p className="text-xs text-[#4ECDC4] uppercase tracking-wide mb-1">Predicted Bone Age</p>
                        <p className="text-3xl font-bold text-white" data-testid="bone-age-result">{boneResult.bone_age_formatted}</p>
                        <p className="text-xs text-white/30 mt-1">({boneResult.bone_age_years} years)</p>
                      </div>
                      <div className="p-4 bg-white/[0.03] rounded-xl border border-white/10 text-center">
                        <p className="text-xs text-white/40 uppercase tracking-wide mb-1">Chronological Age</p>
                        <p className="text-2xl font-bold text-white">{dob ? formatAgeDisplay(dob) : `${boneResult.known_age} years`}</p>
                      </div>
                      <div className="p-4 bg-white/[0.03] rounded-xl border border-white/10 text-center">
                        <p className="text-xs text-white/40 uppercase tracking-wide mb-1">Difference</p>
                        <p className="text-3xl font-bold text-white">{boneResult.age_difference} years</p>
                      </div>
                    </div>

                    <div className={`p-4 rounded-xl border flex items-start gap-3 ${boneResult.is_consistent
                      ? "bg-[#4ECDC4]/10 border-[#4ECDC4]/30"
                      : "bg-[#FF6B6B]/10 border-[#FF6B6B]/30"}`}>
                      {boneResult.is_consistent
                        ? <CheckCircle className="w-5 h-5 text-[#4ECDC4] mt-0.5 shrink-0" />
                        : <AlertTriangle className="w-5 h-5 text-[#FF6B6B] mt-0.5 shrink-0" />}
                      <div>
                        <p className={`font-semibold ${boneResult.is_consistent ? "text-[#4ECDC4]" : "text-[#FF6B6B]"}`}>
                          {boneResult.is_consistent ? "Consistent" : "Attention Required"}
                        </p>
                        <p className="text-white/60 text-sm" data-testid="bone-conclusion">{boneResult.conclusion}</p>
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-3">
                      <Button onClick={downloadPDF} disabled={pdfLoading}
                        className="bg-[#4ECDC4] hover:bg-[#3dbdb5] text-[#0a1628] font-semibold" data-testid="bone-download-pdf-btn">
                        {pdfLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                        Download Bone Age Report (PDF)
                      </Button>
                      <Button onClick={proceedToGrowth}
                        className="bg-white/10 hover:bg-white/20 text-white border border-white/20 font-semibold" data-testid="bridge-to-growth-btn">
                        <Ruler className="w-4 h-4 mr-2" />
                        Also run Growth Assessment <ChevronRight className="w-4 h-4 ml-1" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </main>
      <Footer />
      <Toaster position="top-right" />
      <PaywallModal open={paywallOpen} onOpenChange={setPaywallOpen} balance={creditBalance} />
    </div>
  );
};

export default BoneAgePage;
